/**
 * WordPress dependencies
 */
import {
	RangeControl,
	Flex,
	FlexItem,
	Spacer,
	UnitControl,
	parseQuantityAndUnitFromRawValue,
	useCustomUnits,
	InputControl,
} from '@safe-wordpress/components';
import { forwardRef } from '@safe-wordpress/element';
import { useInstanceId } from '@safe-wordpress/compose';

/**
 * External dependencies
 */
import clsx from 'clsx';
import type { ForwardedRef } from 'react';

/**
 * Internal dependencies
 */
import { Container, Header, HeaderLabel } from './styles';
import type { UnforwardedUnitWithRangeControlProps } from './types';

const DEFAULT_UNITS = [ 'px', 'em', 'rem', 'vw', 'vh' ];

const UnforwardedUnitWithRangeControl = (
	props: UnforwardedUnitWithRangeControlProps,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ref: ForwardedRef< any >
): JSX.Element => {
	const {
		__next40pxDefaultSize = false,
		label,
		disabled,
		onChange,
		size = 'default',
		units: unitsProp = DEFAULT_UNITS,
		initialPosition,
		min,
		max,
		steps,
		value,
	} = props;

	const labelId = useInstanceId(
		UnforwardedUnitWithRangeControl,
		'unit-with-range-control-label'
	);

	const units = useCustomUnits( {
		availableUnits: unitsProp,
	} );

	const [ valueQuantity, originalValueUnit ] =
		parseQuantityAndUnitFromRawValue( value, units );
	const valueUnit = originalValueUnit || units[ 0 ]?.value || '';
	const isValueUnitRelative =
		!! valueUnit && [ 'em', 'rem', 'vw', 'vh' ].includes( valueUnit );

	const hasUnits = typeof value === 'string' && valueUnit;

	return (
		<Container
			ref={ ref }
			className={ clsx( 'components-unit-with-range-control', {
				'nab-components--disabled': disabled,
			} ) }
			// This Container component renders a fieldset element that needs to be labeled.
			aria-labelledby={ labelId }
		>
			<Spacer>
				<Header className="components-unit-with-range-control__header">
					<HeaderLabel id={ labelId }>{ label }</HeaderLabel>
				</Header>
			</Spacer>
			<div>
				<Flex className="components-unit-with-range-control__custom-size-control">
					<FlexItem isBlock>
						{ hasUnits ? (
							<UnitControl
								__next40pxDefaultSize={ __next40pxDefaultSize }
								label={ label }
								disabled={ disabled }
								labelPosition="top"
								hideLabelFromVision
								value={
									valueQuantity
										? `${ valueQuantity }${ valueUnit }`
										: undefined
								}
								onChange={ ( newValue ) =>
									onChange?.( maybeRemoveUnits( newValue ) )
								}
								size={ size }
								units={ hasUnits ? units : [] }
								min={ min }
							/>
						) : (
							<InputControl
								__next40pxDefaultSize={ __next40pxDefaultSize }
								label={ label }
								disabled={ disabled }
								labelPosition="top"
								hideLabelFromVision
								value={ value as string | undefined }
								onChange={ ( newValue ) => {
									if ( newValue === undefined ) {
										onChange?.( undefined );
									} else {
										onChange?.( parseInt( newValue, 10 ) );
									}
								} }
								size={ size }
								min={ min }
							/>
						) }
					</FlexItem>
					<FlexItem isBlock>
						<Spacer marginX={ 2 } marginBottom={ 0 }>
							<RangeControl
								__nextHasNoMarginBottom
								__next40pxDefaultSize={ __next40pxDefaultSize }
								__shouldNotWarnDeprecated36pxSize
								className="components-unit-with-range-control__custom-input"
								label={ label }
								disabled={ disabled }
								hideLabelFromVision
								value={ valueQuantity }
								initialPosition={ initialPosition }
								withInputField={ false }
								onChange={ ( newValue ) => {
									if ( newValue === undefined ) {
										onChange?.( undefined );
									} else if ( hasUnits ) {
										onChange?.(
											newValue +
												( valueUnit ??
													units[ 0 ]?.value ??
													'px' )
										);
									} else {
										onChange?.( newValue );
									}
								} }
								min={ min ?? 0 }
								max={
									max ?? ( isValueUnitRelative ? 10 : 100 )
								}
								step={
									steps ?? ( isValueUnitRelative ? 0.1 : 1 )
								}
							/>
						</Spacer>
					</FlexItem>
				</Flex>
			</div>
		</Container>
	);
};

export const UnitWithRangeControl = forwardRef(
	UnforwardedUnitWithRangeControl
);

export default UnitWithRangeControl;

const maybeRemoveUnits = ( v: string | undefined ) => {
	if ( ! v ) {
		return v;
	}

	if ( /^0[^\d]/.test( v ) ) {
		return '0';
	}

	return v;
};
